import classNames from "classnames/bind";
import type { AbTestVariation } from "../types";
import styles from "./AbTestPanel.module.scss";

const cx = classNames.bind(styles);

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Formats a primitive flag value for display: strings are shown as-is (no quotes),
 * everything else falls back to its JSON representation.
 */
const formatPrimitive = (value: unknown): string =>
	typeof value === "string" ? value : JSON.stringify(value);

type FlagValueProps = {
	value: unknown;
};

/**
 * Renders a flag value: object values are expanded into a key/value list,
 * array values into a bulleted list, primitives are shown inline.
 */
const FlagValue = ({ value }: FlagValueProps) => {
	if (isObject(value)) {
		return (
			<ul className={cx("ab-test-panel__flag-entries")}>
				{Object.entries(value).map(([entryKey, entryValue]) => (
					<li key={entryKey} className={cx("ab-test-panel__flag-entry")}>
						<span className={cx("ab-test-panel__flag-entry-key")}>{entryKey}</span>
						<span className={cx("ab-test-panel__flag-entry-value")}>
							{formatPrimitive(entryValue)}
						</span>
					</li>
				))}
			</ul>
		);
	}

	if (Array.isArray(value)) {
		return (
			<ul className={cx("ab-test-panel__flag-entries")}>
				{value.map((item, index) => (
					<li key={index} className={cx("ab-test-panel__flag-entry")}>
						<span className={cx("ab-test-panel__flag-entry-value")}>
							{formatPrimitive(item)}
						</span>
					</li>
				))}
			</ul>
		);
	}

	return <span className={cx("ab-test-panel__flag-value")}>{formatPrimitive(value)}</span>;
};

type AbTestPanelProps = {
	variations: AbTestVariation[];
	visitorId?: string;
};

/**
 * Displays A/B tests grouped by variation, or nothing when no test is active.
 */
export const AbTestPanel = ({ variations, visitorId }: AbTestPanelProps) => {
	if (variations.length === 0) {
		return null;
	}

	return (
		<section className={cx("ab-test-panel")}>
			<p className={cx("ab-test-panel__title")}>A/B tests</p>

			{visitorId && (
				<div className={cx("ab-test-panel__visitor")}>
					<span className={cx("ab-test-panel__visitor-label")}>ID visiteur</span>
					<span className={cx("ab-test-panel__visitor-id")}>{visitorId}</span>
				</div>
			)}

			<div className={cx("ab-test-panel__list")}>
				{variations.map(variation => (
					<article key={variation.name} className={cx("ab-test-panel__variation")}>
						<header className={cx("ab-test-panel__variation-header")}>
							<span className={cx("ab-test-panel__variation-name")}>
								{variation.name}
							</span>
						</header>

						<ul className={cx("ab-test-panel__flags")}>
							{variation.flags.map(flag => (
								<li key={flag.key} className={cx("ab-test-panel__flag")}>
									<span className={cx("ab-test-panel__flag-key")}>
										{flag.key}
									</span>

									<FlagValue value={flag.value} />
								</li>
							))}
						</ul>
					</article>
				))}
			</div>
		</section>
	);
};
